/**
 * Keeps selection independent from the scene graph so all UI entry points
 * agree about the current objects and their active member.
 */
export function createSelectionState({ allIds, parentOf, onChange = () => {} } = {}) {
  let selected = [];

  function snapshot() {
    return { ids: [...selected], activeId: selected.at(-1) ?? null };
  }

  function emit() {
    onChange(snapshot());
  }

  function known(id) {
    return allIds().includes(id);
  }

  function sameIds(next) {
    return next.length === selected.length && next.every((id, index) => id === selected[index]);
  }

  function commit(next) {
    if (sameIds(next)) return false;
    selected = [...next];
    emit();
    return true;
  }

  return {
    get ids() {
      return [...selected];
    },

    get activeId() {
      return selected.at(-1) ?? null;
    },

    select(id, { toggle = false } = {}) {
      if (!known(id)) return false;
      if (!toggle) return commit([id]);
      return selected.includes(id)
        ? commit(selected.filter((selectedId) => selectedId !== id))
        : commit([...selected, id]);
    },

    selectAll() {
      return commit([...new Set(allIds())]);
    },

    clear() {
      return commit([]);
    },

    prune() {
      const available = new Set(allIds());
      commit(selected.filter((id) => available.has(id)));
      return [...selected];
    },

    normaliseTopLevel() {
      const selectedIds = new Set(selected);
      return selected.filter((id) => {
        for (let parent = parentOf(id); parent; parent = parentOf(parent)) {
          if (selectedIds.has(parent)) return false;
        }
        return true;
      });
    },
  };
}
